import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getFollowers, 
  getFollowing, 
  getFriends,
  followUser,
  unfollowUser,
  searchUsers,
  getSuggestedUsers,
  UserProfile as SocialUserProfile 
} from '../../services/socialService';
import ProfileAvatar from './ProfileAvatar';

// ============================================================================
// FRIENDS MODAL - Production Ready with Search & Suggestions
// ============================================================================

interface FriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'friends' | 'followers' | 'following' | 'discover';
}

const FriendsModal: React.FC<FriendsModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'friends',
}) => {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabIndex, setTabIndex] = useState(
    initialTab === 'friends' ? 0 : 
    initialTab === 'followers' ? 1 : 
    initialTab === 'following' ? 2 : 3
  );
  
  // Social data
  const [friends, setFriends] = useState<SocialUserProfile[]>([]);
  const [followers, setFollowers] = useState<SocialUserProfile[]>([]);
  const [following, setFollowing] = useState<SocialUserProfile[]>([]);
  const [followingMap, setFollowingMap] = useState<{ [userId: string]: boolean }>({});
  
  // Discover tab
  const [searchResults, setSearchResults] = useState<SocialUserProfile[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<SocialUserProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  //  Track loading state per user for follow buttons

  const [followLoadingMap, setFollowLoadingMap] = useState<{ [userId: string]: boolean }>({});
  
  // Scroll container ref for preserving position
  const scrollRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    setLoading(true);
    try {
      const [friendsData, followersData, followingData] = await Promise.all([
        getFriends(currentUser.uid),
        getFollowers(currentUser.uid),
        getFollowing(currentUser.uid),
      ]);

      setFriends(friendsData);
      setFollowers(followersData);
      setFollowing(followingData);

      // Build following map for quick lookup
      const map: { [userId: string]: boolean } = {};
      followingData.forEach(user => {
        map[user.userId] = true;
      });
      setFollowingMap(map);
      
      // Load suggested users for discover tab
      try {
        const suggested = await getSuggestedUsers(currentUser.uid, 10);
        setSuggestedUsers(suggested);
      } catch (err) {
        console.log('Suggested users not available');
      }
    } catch (error) {
      console.error('Error loading social data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setSearchQuery('');
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [isOpen, loadData]);

  // Reset tab index when initialTab changes
  useEffect(() => {
    setTabIndex(
      initialTab === 'friends' ? 0 : 
      initialTab === 'followers' ? 1 : 
      initialTab === 'following' ? 2 : 3
    );
  }, [initialTab]);

  // ============================================================================
  // SEARCH FUNCTIONALITY
  // ============================================================================

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    
    setSearchLoading(true);
    setHasSearched(true);
    
    try {
      const results = await searchUsers(query, currentUser?.uid || '');
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search failed',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setSearchLoading(false);
    }
  }, [currentUser?.uid, toast]);

  // Debounced search
  useEffect(() => {
    if (tabIndex !== 3) return; // Only search in discover tab
    
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
        setHasSearched(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, tabIndex, handleSearch]);

  // ============================================================================
  // FOLLOW/UNFOLLOW - NO RELOAD, OPTIMISTIC UPDATES
  // ============================================================================

  const handleFollow = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser?.uid) return;
    
    //  Set loading state for this specific user

    setFollowLoadingMap(prev => ({ ...prev, [userId]: true }));
    
    //  Optimistic update - don't wait for API

    setFollowingMap(prev => ({ ...prev, [userId]: true }));
    
    try {
      await followUser(currentUser.uid, userId);
      
      //  Update following list locally without reloading

      const newUser = searchResults.find(u => u.userId === userId) || 
                      suggestedUsers.find(u => u.userId === userId) ||
                      followers.find(u => u.userId === userId);
      
      if (newUser) {
        setFollowing(prev => [...prev, newUser]);
      }
      
      //  Check if they follow back - if so, add to friends

      if (followers.some(f => f.userId === userId)) {
        if (newUser) {
          setFriends(prev => [...prev, newUser]);
        }
      }
      
      //  Remove from suggestions since we followed them

      setSuggestedUsers(prev => prev.filter(u => u.userId !== userId));
      
      toast({
        title: 'Following!',
        status: 'success',
        duration: 1500,
        position: 'top',
      });
      
      //  Refresh profile counts in background (non-blocking)

      refreshProfile().catch(() => {});
      
    } catch (error) {
      console.error('Error following user:', error);
      //  Revert on error

      setFollowingMap(prev => ({ ...prev, [userId]: false }));
      toast({
        title: 'Failed to follow',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setFollowLoadingMap(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleUnfollow = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser?.uid) return;
    
    //  Set loading state for this specific user

    setFollowLoadingMap(prev => ({ ...prev, [userId]: true }));
    
    //  Optimistic update

    setFollowingMap(prev => ({ ...prev, [userId]: false }));
    
    try {
      await unfollowUser(currentUser.uid, userId);
      
      //  Update following list locally

      setFollowing(prev => prev.filter(u => u.userId !== userId));
      
      //  Remove from friends if they were mutual

      setFriends(prev => prev.filter(u => u.userId !== userId));
      
      toast({
        title: 'Unfollowed',
        status: 'info',
        duration: 1500,
        position: 'top',
      });
      
      //  Refresh profile counts in background (non-blocking)

      refreshProfile().catch(() => {});
      
    } catch (error) {
      console.error('Error unfollowing user:', error);
      //  Revert on error

      setFollowingMap(prev => ({ ...prev, [userId]: true }));
      toast({
        title: 'Failed to unfollow',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setFollowLoadingMap(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Navigate to user's public profile
  const handleUserClick = (userId: string) => {
    onClose();
    navigate(`/profile/${userId}`);
  };

  // ============================================================================
  // FILTER USERS
  // ============================================================================

  const filterUsers = (users: SocialUserProfile[]) => {
    if (!searchQuery.trim() || tabIndex === 3) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.username.toLowerCase().includes(query)
    );
  };

  // ============================================================================
  // RENDER USER LIST
  // ============================================================================

  const renderUserList = (users: SocialUserProfile[], showFollowButton = true) => {
    const filteredUsers = filterUsers(users);

    if (loading) {
      return (
        <Box py={8} textAlign="center">
          <Spinner color="orange.400" size="lg" />
        </Box>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <Box py={8} textAlign="center">
          <Text color="gray.500" fontSize="md">
            {searchQuery ? 'No users found' : 'No users yet'}
          </Text>
        </Box>
      );
    }

    return (
      <VStack spacing={0} align="stretch">
        {filteredUsers.map(user => (
          <HStack
            key={user.userId}
            py={3}
            px={2}
            borderRadius="lg"
            _hover={{ bg: '#1C1C1E' }}
            _active={{ bg: '#252528' }}
            transition="background 0.2s"
            cursor="pointer"
            onClick={() => handleUserClick(user.userId)}
          >
            <ProfileAvatar
              userId={user.userId}
              photoURL={user.photoURL}
              username={user.username}
              size="md"
            />
            
            <VStack align="start" flex={1} spacing={0} ml={2}>
              <Text color="white" fontWeight="semibold" fontSize="sm">
                {user.username}
              </Text>
              {user.handsWon !== undefined && user.handsWon > 0 && (
                <Text color="gray.500" fontSize="xs">
                  {user.handsWon} hands won
                </Text>
              )}
            </VStack>

            {showFollowButton && user.userId !== currentUser?.uid && (
              <Box
                as="button"
                px={4}
                py={1.5}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="semibold"
                transition="all 0.2s"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  followingMap[user.userId] 
                    ? handleUnfollow(user.userId, e) 
                    : handleFollow(user.userId, e);
                }}
                bg={followingMap[user.userId] ? 'transparent' : 'orange.500'}
                color={followingMap[user.userId] ? 'gray.400' : 'white'}
                border={followingMap[user.userId] ? '1px solid' : 'none'}
                borderColor="gray.600"
                opacity={followLoadingMap[user.userId] ? 0.6 : 1}
                pointerEvents={followLoadingMap[user.userId] ? 'none' : 'auto'}
                minW="80px"
                textAlign="center"
                _hover={{
                  bg: followingMap[user.userId] ? 'red.900' : 'orange.600',
                  color: followingMap[user.userId] ? 'red.400' : 'white',
                  borderColor: followingMap[user.userId] ? 'red.600' : 'transparent',
                }}
                _active={{ transform: 'scale(0.95)' }}
              >
                {followLoadingMap[user.userId] ? (
                  <Spinner size="xs" />
                ) : (
                  followingMap[user.userId] ? 'Following' : 'Follow'
                )}
              </Box>
            )}
          </HStack>
        ))}
      </VStack>
    );
  };

  // ============================================================================
  // RENDER DISCOVER TAB
  // ============================================================================

  const renderDiscoverTab = () => {
    return (
      <VStack spacing={4} align="stretch" px={4} py={2}>
        {/* Search Results or Suggestions */}
        {hasSearched ? (
          <>
            {searchLoading ? (
              <Box py={8} textAlign="center">
                <Spinner color="orange.400" size="lg" />
                <Text color="gray.500" mt={2}>Searching...</Text>
              </Box>
            ) : searchResults.length > 0 ? (
              <>
                <Text color="gray.400" fontSize="xs" textTransform="uppercase">
                  Search Results ({searchResults.length})
                </Text>
                {renderUserList(searchResults, true)}
              </>
            ) : (
              <Box py={8} textAlign="center">
                <Text fontSize="3xl" mb={2}></Text>

                <Text color="gray.500" fontSize="md">
                  No users found for "{searchQuery}"
                </Text>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Try a different search term
                </Text>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Suggested Users */}
            {suggestedUsers.length > 0 && (
              <>
                <HStack justify="space-between">
                  <Text color="gray.400" fontSize="xs" textTransform="uppercase">
                    Suggested For You
                  </Text>
                  <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                    {suggestedUsers.length} players
                  </Badge>
                </HStack>
                {renderUserList(suggestedUsers, true)}
              </>
            )}
            
            {/* Empty state */}
            {suggestedUsers.length === 0 && !loading && (
              <Box py={8} textAlign="center">
                <Text fontSize="3xl" mb={2}></Text>

                <Text color="gray.500" fontSize="md">
                  Search for players
                </Text>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Type at least 2 characters to search
                </Text>
              </Box>
            )}
          </>
        )}
      </VStack>
    );
  };

  // ============================================================================
  // COUNTS
  // ============================================================================

  const displayFriendsCount = friends.length;
  const displayFollowersCount = followers.length;
  const displayFollowingCount = following.length;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Box w="60px" />
          <Text color="white" fontSize="md" fontWeight="semibold">
            {userProfile?.username || 'Profile'}
          </Text>
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={onClose}
            _hover={{ color: 'white' }}
            w="60px"
            textAlign="right"
          >
            Done
          </Text>
        </HStack>

        <ModalBody p={0} overflowY="auto" ref={scrollRef}>
          {/* Stats */}
          <HStack justify="center" py={4} spacing={12} borderBottom="1px solid" borderColor="gray.800">
            {loading ? (
              <Spinner color="orange.400" size="sm" />
            ) : (
              <>
                <VStack spacing={0} cursor="pointer" onClick={() => setTabIndex(0)}>
                  <Text color="white" fontSize="xl" fontWeight="bold">
                    {displayFriendsCount}
                  </Text>
                  <Text color="gray.500" fontSize="sm">Friends</Text>
                </VStack>
                <VStack spacing={0} cursor="pointer" onClick={() => setTabIndex(1)}>
                  <Text color="white" fontSize="xl" fontWeight="bold">
                    {displayFollowersCount}
                  </Text>
                  <Text color="gray.500" fontSize="sm">Followers</Text>
                </VStack>
                <VStack spacing={0} cursor="pointer" onClick={() => setTabIndex(2)}>
                  <Text color="white" fontSize="xl" fontWeight="bold">
                    {displayFollowingCount}
                  </Text>
                  <Text color="gray.500" fontSize="sm">Following</Text>
                </VStack>
              </>
            )}
          </HStack>

          {/* Search */}
          <Box px={4} py={3}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="100%">
                <Search size={18} color="#666" />
              </InputLeftElement>
              <Input
                placeholder={tabIndex === 3 ? "Search for players..." : "Search"}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="#1C1C1E"
                border="none"
                borderRadius="xl"
                color="white"
                py={5}
                _placeholder={{ color: 'gray.500' }}
                _focus={{ 
                  boxShadow: '0 0 0 1px rgba(255, 107, 53, 0.5)',
                  bg: '#252528',
                }}
              />
            </InputGroup>
          </Box>

          {/* Tabs */}
          <Tabs 
            index={tabIndex} 
            onChange={setTabIndex}
            variant="unstyled"
            isFitted
          >
            <TabList borderBottom="1px solid" borderColor="gray.800">
              <Tab
                color="gray.500"
                fontWeight="semibold"
                py={3}
                _selected={{ 
                  color: 'white',
                  borderBottom: '2px solid',
                  borderColor: 'orange.400',
                }}
              >
                <HStack spacing={1}>
                  <Heart size={14} />
                  <Text>Friends</Text>
                </HStack>
              </Tab>
              <Tab
                color="gray.500"
                fontWeight="semibold"
                py={3}
                _selected={{ 
                  color: 'white',
                  borderBottom: '2px solid',
                  borderColor: 'orange.400',
                }}
              >
                Followers
              </Tab>
              <Tab
                color="gray.500"
                fontWeight="semibold"
                py={3}
                _selected={{ 
                  color: 'white',
                  borderBottom: '2px solid',
                  borderColor: 'orange.400',
                }}
              >
                Following
              </Tab>
              <Tab
                color="gray.500"
                fontWeight="semibold"
                py={3}
                _selected={{ 
                  color: 'white',
                  borderBottom: '2px solid',
                  borderColor: 'cyan.400',
                }}
              >
                <HStack spacing={1}>
                  <UserPlus size={14} />
                  <Text>Discover</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={4}>
                {/* Friends Tab */}
                {renderUserList(friends, true)}
                {!loading && friends.length === 0 && !searchQuery && (
                  <VStack py={8} spacing={2}>
                    <Text fontSize="3xl"></Text>

                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      Friends are people who follow each other.
                      <br />
                      Start following players to build your network!
                    </Text>
                  </VStack>
                )}
              </TabPanel>
              
              <TabPanel px={4}>
                {/* Followers Tab */}
                {renderUserList(followers, true)}
                {!loading && followers.length === 0 && !searchQuery && (
                  <VStack py={8} spacing={2}>
                    <Text fontSize="3xl"></Text>

                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      No followers yet.
                      <br />
                      Play more games to get noticed!
                    </Text>
                  </VStack>
                )}
              </TabPanel>
              
              <TabPanel px={4}>
                {/* Following Tab */}
                {renderUserList(following, true)}
                {!loading && following.length === 0 && !searchQuery && (
                  <VStack py={8} spacing={2}>
                    <Text fontSize="3xl"></Text>

                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      You're not following anyone yet.
                      <br />
                      Find players at the tables!
                    </Text>
                  </VStack>
                )}
              </TabPanel>
              
              <TabPanel p={0}>
                {/* Discover Tab */}
                {renderDiscoverTab()}
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Spacer for safe area */}
          <Box h="100px" />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FriendsModal;